/**
 * @file timesheetTime.js
 * @description Pure date / time / money utilities for the Timesheet Tracker.
 *   Ported from the founder's ClockIn prototype (src/lib/time.js) and extended
 *   with explicit, timezone-safe date-key helpers.
 *
 *   FIRM DATE RULE: all dates in the Tracker are `YYYY-MM-DD` local date
 *   strings. We never do timezone math on the date field. Date arithmetic is
 *   done by parsing as a LOCAL midnight (`${key}T00:00:00`) so a key in →
 *   key out is stable regardless of the user's timezone.
 *
 *   MONEY RULE: money/rates are stored as fractional `numeric`. We round to
 *   cents (2 dp) at the moment income is computed for storage, and again for
 *   display. roundMoney is the single rounding primitive.
 *
 * @module lib/timesheetTime
 */

const MINUTES_IN_DAY = 24 * 60;
const SLOT_MINUTES = 15;

// ─── Money ──────────────────────────────────────────────────────────────────

/** Round a money value to cents. Uses the epsilon trick to avoid FP drift. */
export function roundMoney(value) {
  return Math.round(((value || 0) + Number.EPSILON) * 100) / 100;
}

// ─── Core money/units math (trust-critical) ─────────────────────────────────

/** Income for a session = hours × rate, rounded to cents. */
export function computeIncome(durationMinutes, rateSnapshot) {
  return roundMoney(((durationMinutes || 0) / 60) * (rateSnapshot || 0));
}

/** Billable units = duration / 15 (15-minute units), rounded to 2 dp. */
export function computeUnits(durationMinutes) {
  return roundMoney(((durationMinutes || 0) / 15));
}

/**
 * Duration between two clock-minute offsets. If end <= start the session is
 * assumed to wrap past midnight (e.g. 11:30pm → 12:30am).
 */
export function computeDuration(startMinutes, endMinutes) {
  if (startMinutes == null || endMinutes == null) return 0;
  if (endMinutes <= startMinutes) return endMinutes + MINUTES_IN_DAY - startMinutes;
  return endMinutes - startMinutes;
}

// ─── Date keys (YYYY-MM-DD, no timezone math) ───────────────────────────────

/** Format a JS Date as a local YYYY-MM-DD key (no UTC shift). */
export function toDateKey(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Today's local date key. */
export function todayKey() {
  return toDateKey(new Date());
}

/** Parse a YYYY-MM-DD key to a local-midnight Date (no UTC shift). */
export function dateKeyToDate(dateKey) {
  if (!dateKey) return null;
  return new Date(`${dateKey}T00:00:00`);
}

/** Add `amount` whole days to a date key, returning a new date key. */
export function addDaysToDateKey(dateKey, amount) {
  const d = dateKeyToDate(dateKey);
  if (!d) return dateKey;
  d.setDate(d.getDate() + amount);
  return toDateKey(d);
}

/** Whole-day difference (endKey - startKey). Negative if end precedes start. */
export function getDateKeyDifference(startKey, endKey) {
  const start = dateKeyToDate(startKey);
  const end = dateKeyToDate(endKey);
  if (!start || !end) return null;
  return Math.round((end - start) / 86400000);
}

// ─── Date helpers (Date objects) ────────────────────────────────────────────

export function addDays(date, amount) {
  const value = new Date(date);
  value.setDate(value.getDate() + amount);
  return value;
}

/** Monday-anchored start of week, matching the prototype's behavior. */
export function startOfWeek(date) {
  const value = new Date(date);
  const day = value.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diff);
  value.setHours(0, 0, 0, 0);
  return value;
}

/** First day of the month containing `date`, at local midnight. */
export function startOfMonth(date) {
  const value = new Date(date);
  value.setDate(1);
  value.setHours(0, 0, 0, 0);
  return value;
}

/** Add `amount` months (preserving day-of-month where possible). */
export function addMonths(date, amount) {
  const value = new Date(date);
  value.setMonth(value.getMonth() + amount);
  return value;
}

// ─── Display formatters ─────────────────────────────────────────────────────

/** "Mon, Jan 6" — short weekday + month + day. */
export function formatDateLabel(date) {
  return new Date(date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** "January 2026" — full month + year. */
export function formatMonthLabel(date) {
  return new Date(date).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export function minutesToClock(minutes, use24Hour) {
  const safe = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  const hour = Math.floor(safe / 60);
  const minute = safe % 60;
  if (use24Hour) {
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  }
  const suffix = hour >= 12 ? "PM" : "AM";
  const twelveHour = hour % 12 || 12;
  return `${twelveHour}:${minute.toString().padStart(2, "0")} ${suffix}`;
}

/**
 * Parse a free-text time like "9", "9:30", "9am", "2:15 pm" → minutes since
 * midnight, or null if unparseable.
 */
export function parseTimeInput(value) {
  if (!value || !value.trim()) return null;
  const source = value.trim().toLowerCase();
  const match = source.match(/^(\d{1,2})(?::(\d{2}))?\s*(a|am|p|pm)?$/);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] ?? "0");
  const meridiem = match[3];
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem.startsWith("p") && hour !== 12) hour += 12;
    if (meridiem.startsWith("a") && hour === 12) hour = 0;
  } else if (hour > 23) {
    return null;
  }
  return hour * 60 + minute;
}

// ─── Small functional helpers ───────────────────────────────────────────────

export function sum(items, getValue) {
  return (items || []).reduce((total, item) => total + (getValue(item) || 0), 0);
}

export function groupBy(items, getKey) {
  return (items || []).reduce((groups, item) => {
    const key = getKey(item);
    groups[key] = groups[key] || [];
    groups[key].push(item);
    return groups;
  }, {});
}

export { MINUTES_IN_DAY, SLOT_MINUTES };
